import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, PlayCircle, Timer, ShieldCheck, Activity } from "lucide-react";
import axios from "axios";
import { DashboardPreview } from "./DashboardPreview";
import { scrollToId } from "@/lib/scroll";
import { num } from "@/lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-2 -mb-2">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const fadeUp = (delay) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] },
});

export const Hero = () => {
  const previewRef = useRef(null);
  const [total, setTotal] = useState(0);
  const { scrollYProgress } = useScroll({ target: previewRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [70, -70]);
  const rotate = useTransform(scrollYProgress, [0, 1], [1.5, -1.5]);

  useEffect(() => {
    axios
      .get(`${API}/analyses/stats`)
      .then((r) => setTotal(r.data?.total || 0))
      .catch(() => {});
  }, []);

  return (
    <section data-testid="hero-section" className="relative overflow-hidden">
      <div className="hero-grid-bg absolute inset-0" aria-hidden="true" />
      <div className="absolute -top-32 left-1/2 h-[420px] w-[720px] -translate-x-1/2 rounded-full bg-emerald-400/15 blur-[120px]" aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-4 pb-16 pt-16 sm:px-6 sm:pt-24 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <motion.div {...fadeUp(0.05)} className="mb-6 inline-flex">
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1.5 text-xs font-semibold text-emerald-700">
              <Activity className="h-3.5 w-3.5" />
              Tecnología financiera para inversores inmobiliarios
            </span>
          </motion.div>

          <h1 className="font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-slate-900 sm:text-5xl lg:text-7xl">
            <MaskedLine delay={0.1}>¿Merece la pena{" "}</MaskedLine>
            <MaskedLine delay={0.22}>
              este{" "}
              <span className="relative inline-block">
                <span className="relative z-10">piso?</span>
                <motion.svg
                  className="absolute -bottom-2 left-0 z-0 w-full"
                  viewBox="0 0 220 14"
                  fill="none"
                  aria-hidden="true"
                >
                  <motion.path
                    d="M4 10 C 60 3, 160 3, 216 8"
                    stroke="#10B981"
                    strokeWidth="7"
                    strokeLinecap="round"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.7, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
                  />
                </motion.svg>
              </span>
              {" "}
            </MaskedLine>
            <MaskedLine delay={0.34}>
              <span className="mt-2 block text-2xl font-bold leading-snug text-slate-500 sm:text-3xl lg:text-4xl">
                Calcula su <span className="text-emerald-500">rentabilidad</span>
              </span>
            </MaskedLine>
          </h1>

          <motion.p {...fadeUp(0.45)} className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
            Analiza la rentabilidad, cash flow y riesgo de cualquier inversión inmobiliaria.
          </motion.p>

          <motion.div {...fadeUp(0.58)} className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <button
              data-testid="hero-primary-cta"
              onClick={() => scrollToId("#analisis")}
              className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-emerald-500 px-8 py-4 text-sm font-semibold text-white shadow-xl shadow-emerald-500/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-emerald-600 hover:shadow-2xl hover:shadow-emerald-500/30 sm:w-auto"
            >
              Analizar inversión
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-secondary-cta"
              onClick={() => scrollToId("#herramientas")}
              className="group inline-flex w-full items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-8 py-4 text-sm font-semibold text-slate-700 transition-all duration-300 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-lg hover:shadow-slate-900/5 sm:w-auto"
            >
              <PlayCircle className="h-4 w-4 text-emerald-500" />
              Ver cómo funciona
            </button>
          </motion.div>

          <motion.div {...fadeUp(0.72)} className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
            <span className="inline-flex items-center gap-2 text-sm text-slate-500">
              <Timer className="h-4 w-4 text-emerald-500" /> 2 min por análisis
            </span>
            <span className="inline-flex items-center gap-2 text-sm text-slate-500">
              <ShieldCheck className="h-4 w-4 text-emerald-500" /> Datos del mercado español
            </span>
            <span className="inline-flex items-center gap-2 text-sm text-slate-500" data-testid="live-stats-chip">
              <Activity className="h-4 w-4 text-emerald-500" />
              {total > 0 ? (
                <>
                  <span className="font-mono font-semibold text-slate-900">{num(total)}</span> análisis realizados
                </>
              ) : (
                "Análisis en tiempo real"
              )}
            </span>
          </motion.div>
        </div>

        <motion.div
          ref={previewRef}
          style={{ y, rotate }}
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="mt-14 sm:mt-20"
        >
          <div className="animate-inmo-float">
            <DashboardPreview />
          </div>
        </motion.div>
      </div>
    </section>
  );
};
